'use client';
import React, { useEffect, useState } from 'react';
import { getAllAthletesLatestWellness } from '@/lib/wellnessService';
import type { WellnessWithAthlete } from '@/lib/wellnessService';
import { STATUS_META } from '@/lib/wellness';
import type { ReadinessStatus } from '@/lib/wellness';
import { AlertTriangle, Activity, Moon, Brain, Dumbbell, MessageSquare, RefreshCw } from 'lucide-react';

const STATUS_ORDER: Record<ReadinessStatus, number> = { red: 0, orange: 1, yellow: 2, green: 3 };

export default function CoachWellnessPanel() {
  const [entries, setEntries] = useState<WellnessWithAthlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | ReadinessStatus>('all');

  const load = async () => {
    setLoading(true);
    const { data, error: err } = await getAllAthletesLatestWellness();
    setLoading(false);
    if (err) { setError(err); return; }
    setEntries(data);
  };

  useEffect(() => { load(); }, []);

  const filtered = entries
    .filter(e => filter === 'all' || e.readiness_status === filter)
    .sort((a, b) => STATUS_ORDER[a.readiness_status as ReadinessStatus] - STATUS_ORDER[b.readiness_status as ReadinessStatus]);

  const counts = {
    red: entries.filter(e => e.readiness_status === 'red').length,
    orange: entries.filter(e => e.readiness_status === 'orange').length,
    yellow: entries.filter(e => e.readiness_status === 'yellow').length,
    green: entries.filter(e => e.readiness_status === 'green').length,
  };

  const painFlags = entries.filter(e => e.body_pain).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="section-header">Daily Wellness Panel</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{entries.length} athletes · Latest check-in per athlete</p>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          { status: 'green' as ReadinessStatus, count: counts.green, label: 'Ready' },
          { status: 'yellow' as ReadinessStatus, count: counts.yellow, label: 'Monitor' },
          { status: 'orange' as ReadinessStatus, count: counts.orange, label: 'Modify' },
          { status: 'red' as ReadinessStatus, count: counts.red, label: 'Recovery' },
        ]).map(({ status, count, label }) => {
          const m = STATUS_META[status];
          return (
            <button
              key={status}
              onClick={() => setFilter(filter === status ? 'all' : status)}
              className={`rounded-xl border p-4 text-left transition-all duration-150 ${
                filter === status ? 'ring-1' : 'hover:border-zinc-600'
              }`}
              style={{
                background: m.bg,
                borderColor: filter === status ? m.color : m.border,
                boxShadow: filter === status ? `0 0 0 1px ${m.color}` : undefined,
              }}
            >
              <p className="text-2xl font-black" style={{ color: m.color }}>{count}</p>
              <p className="text-xs font-semibold mt-0.5" style={{ color: m.color }}>{label}</p>
            </button>
          );
        })}
      </div>

      {/* Pain Alert */}
      {painFlags > 0 && (
        <div className="rounded-xl border border-red-400/30 bg-red-400/10 p-4 flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-300">
            <span className="font-bold">{painFlags} athlete{painFlags > 1 ? 's' : ''}</span> reported body pain today — review immediately.
          </p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="card p-8 flex items-center justify-center">
          <RefreshCw size={20} className="animate-spin text-zinc-500" />
        </div>
      ) : error ? (
        <div className="card p-6 text-center text-sm text-red-400">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center text-sm text-zinc-500">No wellness check-ins found.</div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin rounded-xl border border-zinc-800">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-800/40">
                {['Athlete', 'Status', 'Score', 'Soreness', 'Stress', 'Sleep', 'Pain', 'Game Flag', 'Recommendation', 'Notes'].map(h => (
                  <th key={h} className="text-left py-3 px-3 text-xs font-medium uppercase tracking-wider text-zinc-500 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const m = STATUS_META[row.readiness_status as ReadinessStatus];
                const athleteName = row.athlete_profiles?.name ?? 'Unknown';
                const initials = row.athlete_profiles?.avatar_initials ?? athleteName.slice(0, 2).toUpperCase();
                const isToday = row.checkin_date === new Date().toISOString().split('T')[0];

                return (
                  <tr key={row.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                    {/* Athlete */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-zinc-400">{initials}</span>
                        </div>
                        <div>
                          <p className="font-medium text-zinc-200 whitespace-nowrap">{athleteName}</p>
                          <p className="text-xs text-zinc-600">
                            {isToday ? 'Today' : new Date(row.checkin_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-3 px-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap"
                        style={{ color: m.color, background: m.bg, borderColor: m.border }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                        {m.label}
                      </span>
                    </td>

                    {/* Score */}
                    <td className="py-3 px-3">
                      <span className="text-lg font-black" style={{ color: m.color }}>{row.readiness_score}</span>
                    </td>

                    {/* Soreness */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <Activity size={12} className="text-zinc-500" />
                        <span className={`text-sm font-semibold ${row.soreness_level >= 4 ? 'text-red-400' : row.soreness_level >= 3 ? 'text-amber-400' : 'text-zinc-300'}`}>
                          {row.soreness_level}/5
                        </span>
                      </div>
                    </td>

                    {/* Stress */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <Brain size={12} className="text-zinc-500" />
                        <span className={`text-sm font-semibold ${row.stress_level >= 4 ? 'text-red-400' : row.stress_level >= 3 ? 'text-amber-400' : 'text-zinc-300'}`}>
                          {row.stress_level}/5
                        </span>
                      </div>
                    </td>

                    {/* Sleep */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <Moon size={12} className="text-zinc-500" />
                        <span className="text-sm text-zinc-300">{row.sleep_duration}h</span>
                      </div>
                    </td>

                    {/* Pain */}
                    <td className="py-3 px-3">
                      {row.body_pain ? (
                        <div className="flex items-center gap-1">
                          <AlertTriangle size={13} className="text-red-400" />
                          <span className="text-xs text-red-400 font-semibold">{row.pain_location || 'Yes'}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-600">—</span>
                      )}
                    </td>

                    {/* Game Flag */}
                    <td className="py-3 px-3">
                      {row.practice_today ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)', color: '#E10600' }}
                        >
                          <Dumbbell size={10} /> Today
                        </span>
                      ) : row.practice_yesterday ? (
                        <span className="text-xs text-zinc-500">Yesterday</span>
                      ) : (
                        <span className="text-xs text-zinc-600">—</span>
                      )}
                    </td>

                    {/* Recommendation */}
                    <td className="py-3 px-3 max-w-[220px]">
                      <p className="text-xs text-zinc-400 line-clamp-2">{row.training_adjustment}</p>
                    </td>

                    {/* Notes */}
                    <td className="py-3 px-3 max-w-[160px]">
                      {row.notes_to_coach ? (
                        <div className="flex items-start gap-1">
                          <MessageSquare size={11} className="text-zinc-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-zinc-400 line-clamp-2">{row.notes_to_coach}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
